<template>
    <div>
        <v-dialog v-model="dialog_anamnese" max-width="1200px" persistent
            :fullscreen="$vuetify.breakpoint.xsOnly || isFullscreen">
            <v-card>
                <v-card-title class="mr-4 pr-0">
                    <span class="text-h5">Vorgeschichte / Anamnese bearbeiten</span>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom open-delay="300" v-if="$vuetify.breakpoint.smAndUp">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon @click="toggleFullscreen" v-bind="attrs" v-on="on">
                                <v-icon>{{ isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                            </v-btn>
                        </template>
                        {{ isFullscreen ? 'Vollbild beenden.' : 'In den Vollbildmodus wechseln.' }}
                    </v-tooltip>
                </v-card-title>
                <v-card-text>
                    <TipTapEditor 
                        ref="editor-anamnese" 
                        :value="edit_anamnese"
                        @input="handleInput('edit_anamnese', $event)"
                        :session="session"
                        @showError="$emit('showError', $event)"
                        @showInfo="$emit('showInfo', $event)"
                    />
                </v-card-text>
                <v-card-actions v-if="isEditable" class="pb-5 px-6">
                    <v-spacer></v-spacer>
                    <v-btn text :color="$store.state.theme.primary" @click="abortAnamnese">Abbrechen</v-btn>
                    <v-btn outlined :color="$store.state.theme.green" @click="saveAnamnese"
                        :loading="saving">Speichern</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialog_documentation" max-width="1200px" persistent
            :fullscreen="$vuetify.breakpoint.xsOnly || isFullscreen">
            <v-card>
                <v-tabs v-model="tabs_documentation_type" color="grey darken-4"
                    :background-color="$store.state.theme.background_tabs" grow class="mb-5" show-arrows>
                    <v-tabs-slider color="grey darken-4" />
                    <v-tab>
                        Dokumentation
                        <v-tooltip bottom open-delay="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon right v-bind="attrs" v-on="on">
                                    mdi-information-outline
                                </v-icon>
                            </template>
                            <span>
                                Erstelle hier die Dokumentation des Termins. 
                                Diese kann bei Bedarf vom Klienten angefordert und exportiert werden.
                            </span>
                        </v-tooltip>
                    </v-tab>
                    <v-tab>
                        Persönliche Notizen
                        <v-tooltip bottom open-delay="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon right v-bind="attrs" v-on="on">
                                    mdi-information-outline
                                </v-icon>
                            </template>
                            <span>
                                Schreibe hier persönliche Notizen zum Termin. 
                                Diese sind nicht Teil der offiziellen Dokumentation und werden nicht exportiert.
                            </span>
                        </v-tooltip>
                    </v-tab>
                    <v-tooltip bottom open-delay="300" v-if="$vuetify.breakpoint.smAndUp">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon @click="toggleFullscreen" class="mt-2 mx-3" v-bind="attrs" v-on="on">
                                <v-icon>{{ isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                            </v-btn>
                        </template>
                        {{ isFullscreen ? 'Vollbild beenden.' : 'In den Vollbildmodus wechseln.' }}
                    </v-tooltip>
                </v-tabs>

                <v-card-text class="pt-1">
                    <v-tabs-items v-model="tabs_documentation_type">
                        <v-tab-item eager>
                            <TipTapEditor 
                                ref="editor-documentation" 
                                :value="documentation"
                                @input="handleInput('documentation', $event)"
                                @showError="$emit('showError', $event)"
                                @showInfo="$emit('showInfo', $event)"
                                :session="session" 
                            />
                        </v-tab-item>
                        <v-tab-item eager>
                            <TipTapEditor 
                                ref="editor-notes" 
                                :value="notes"
                                @input="handleInput('notes', $event)"
                                @showError="$emit('showError', $event)"
                                @showInfo="$emit('showInfo', $event)"
                                :session="session"
                            />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>

                <v-card-actions class="pb-5 px-6">
                    <v-spacer></v-spacer>
                    <v-btn text :color="$store.state.theme.primary" @click="abortDocumentation">Abbrechen</v-btn>
                    <v-btn outlined :color="$store.state.theme.green" @click="saveDocumentation"
                        :loading="saving">Speichern</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <p class="d-flex align-center text-h6 text-sm-h5">
            <v-icon left class="mr-2" color="black">mdi-clipboard-account-outline</v-icon>
            <span class="text-h6 text-sm-h5 black--text">Vorgeschichte / Anamnese</span>
            <v-spacer/>
            <!-- Export Dropdown Menu -->
            <v-menu 
                offset-y 
                open-on-hover
                :close-on-content-click="false" 
                transition="slide-y-transition"
                v-if="clientStatistics && isEditable"
                :disabled="loading_client_data || error_loading_anamnese || error_loading_documentation"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        class="ml-5"
                        small
                        :loading="exporting"
                        :disabled="loading_client_data || error_loading_anamnese || error_loading_documentation"
                    >
                        <v-icon :left="$vuetify.breakpoint.smAndUp">
                            mdi-clipboard-arrow-down-outline
                        </v-icon>
                        <span v-if="$vuetify.breakpoint.smAndUp" class="mr-1">Dokumentation exportieren</span>
                        <v-icon small>mdi-chevron-down</v-icon>
                    </v-btn>
                </template>
                
                <v-card>
                    <v-list dense>
                        <v-list-item class="px-4">
                            <v-checkbox
                                v-model="anonymizeExport"
                                label="Name anonymisieren"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                            ></v-checkbox>
                             <v-tooltip right open-delay="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small class="ml-2" color="grey darken-1" v-bind="attrs" v-on="on">
                                        mdi-information-outline
                                    </v-icon>
                                </template>
                                <span>Wenn aktiviert, wird der Name des Klienten anonymisiert (z.B. MaMu).</span>
                            </v-tooltip>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item @click="triggerExport(false)" :disabled="exporting">
                            <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-clipboard-text-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Dokumentation ohne persönlichen Notizen</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="triggerExport(true)" :disabled="exporting">
                            <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-clipboard-text-multiple-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Dokumentation mit persönlichen Notizen</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-menu>
            <!-- End Export Dropdown Menu -->
        </p>
        <v-card>
            <!-- Add Anamnese Tabs above the Anamnese card -->
            <v-tabs v-if="linkedClients.length > 0"
                    v-model="selectedAnamneseTab"
                    background-color="grey lighten-4"
                    color="primary"
                    show-arrows
                    class="mb-2">
                <v-tab v-for="tab in allAnamneseTabs" :key="tab.id" :tab-value="tab.id">
                    <v-icon left>
                        {{ getRelationIcon(tab.relation) }}
                    </v-icon>
                    {{ tab.name }}
                </v-tab>
            </v-tabs>
            <v-card-text class="pb-5 text-body-1 black--text" ref="container-anamnese">
                <v-skeleton-loader v-show="loading_client_data" type="paragraph" transition="fade-transition" />
                <template v-if="!loading_client_data">
                    <TipTapViewer 
                        :content="getAnamneseForTab(selectedAnamneseTab)" 
                        :expanded="showFullAnamneseMap[selectedAnamneseTab]"
                        @long-content="handleLongContent"
                        ref="anamneseViewer"
                    />
                </template>
                <span v-if="!loading_client_data && !getAnamneseForTab(selectedAnamneseTab) && !error_loading_anamnese" class="font-italic">
                    Es wurde noch keine Anamnese für diesen Klienten verfasst.
                </span>
                <span v-if="!loading_client_data && !getAnamneseForTab(selectedAnamneseTab) && error_loading_anamnese" class="font-italic">
                    <v-icon left>mdi-connection</v-icon>
                    Die Anamnese konnte nicht geladen werden.<br />
                    <v-btn outlined :color="$store.state.theme.green" class="mt-5" @click="reloadWindow">
                        <v-icon left>
                            mdi-refresh
                        </v-icon>
                        Neu Laden
                    </v-btn>
                </span>
            </v-card-text>
            <v-card-actions class="justify-end px-5 pb-5" v-if="!error_loading_anamnese">
                <v-btn
                    v-if="anamneseIsLongMap[selectedAnamneseTab]"
                    text
                    :color="$store.state.theme.primary"
                    @click="toggleAnamneseView"
                >
                    {{ showFullAnamneseMap[selectedAnamneseTab] ? 'Weniger anzeigen' : 'Alles anzeigen' }}
                    <v-icon right>{{ showFullAnamneseMap[selectedAnamneseTab] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isEditable && selectedAnamneseTab === clientId"
                    outlined
                    @click="editAnamnese"
                    :disabled="loading_client_data"
                >
                    Bearbeiten
                </v-btn>
            </v-card-actions>
        </v-card>

        <div class="d-flex align-center mt-10 mb-5 full-width flex-wrap">
            <v-icon left class="mr-2" color="black">mdi-chart-timeline-variant</v-icon>
            <span class="text-h6 text-sm-h5 black--text">Behandlungsverlauf</span>
            <v-btn icon class="mx-2" @click="sort_appointments_descending = !sort_appointments_descending">
                <v-icon>
                    {{ sort_appointments_descending ? 'mdi-sort-calendar-descending' : 'mdi-sort-calendar-ascending' }}
                </v-icon>
            </v-btn>
            <v-spacer />
            <v-chip-group
                v-if="linkedClients.length > 0"
                v-model="selectedAppointmentClientId"
                mandatory
                show-arrows
            >
                <v-chip 
                    :value="null"
                    outlined
                    :small="$vuetify.breakpoint.smAndDown"
                >
                    <v-icon left color="grey darken-1" :small="$vuetify.breakpoint.smAndDown">
                        mdi-human-queue
                    </v-icon>
                    Alle
                </v-chip>
                <v-chip 
                    :value="clientId"
                    :color="getRelationColor('klient')"
                    outlined
                    :small="$vuetify.breakpoint.smAndDown"
                >
                    <v-icon left color="success" :small="$vuetify.breakpoint.smAndDown">
                        {{ getRelationIcon('klient') }}
                    </v-icon>
                    {{ clientData.vorname }} {{ clientData.nachname }}
                </v-chip>
                <v-chip 
                    v-for="client in linkedClients" 
                    :key="client.id" 
                    :value="client.id" 
                    :color="getRelationColor(client.relation)" 
                    outlined
                    :small="$vuetify.breakpoint.smAndDown"
                >
                    <v-icon left :color="getRelationColor(client.relation)" :small="$vuetify.breakpoint.smAndDown">
                        {{ getRelationIcon(client.relation) }}
                    </v-icon>
                    {{ client.vorname }} {{ client.nachname }}
                </v-chip>
            </v-chip-group>
        </div>

        <v-card v-for="appointment in filteredAppointments" :key="appointment.id + '-' + appointment.clientId" class="mb-5">
            <v-card-title>
                <div class="d-flex flex-column">
                    <div class="d-flex align-center">
                        <span class="text-subtitle-1 font-weight-bold">
                            {{ appointment.appointment_count }}. Termin von 
                        </span>
                        <v-tooltip v-if="appointment.isCoupleAppointment" bottom open-delay="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip 
                                    :color="getRelationColor(appointment.relation)" 
                                    dark 
                                    small 
                                    class="mx-2"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon left small>
                                        {{ getRelationIcon(appointment.relation) }}
                                    </v-icon>
                                    {{ appointment.clientName }}
                                </v-chip>
                            </template>
                            <div>
                                <div><strong>{{ getPrimaryClientName(appointment) }}:</strong> Hauptklient</div>
                                <div><strong>{{ getPartnerName(appointment) }}:</strong> Verknüpfter Partner</div>
                                <div class="mt-1 text-caption">Der Termin ist {{ getPrimaryClientName(appointment) }} zugeordnet.</div>
                                <div v-if="appointment.isLinked" class="mt-1 text-caption">Bearbeitung nur in der Dokumentation von {{ getPrimaryClientName(appointment) }} möglich.</div>
                            </div>
                        </v-tooltip>
                        <v-chip 
                            v-else
                            :color="getRelationColor(appointment.relation)" 
                            dark 
                            small 
                            class="mx-2"
                        >
                            <v-icon left small>
                                {{ getRelationIcon(appointment.relation) }}
                            </v-icon>
                            {{ appointment.clientName }}
                        </v-chip>
                        <span class="text-subtitle-1 font-weight-bold">
                            am {{ getFormattedDate(appointment.datum) }} um {{ getFormattedTime(appointment.uhrzeit) }}
                        </span>
                    </div>
                    <span class="text-subtitle-2 d-flex align-center" v-if="appointment.bezeichnung">
                        <v-icon left small>mdi-handshake-outline</v-icon>
                        {{ appointment.bezeichnung }}
                        <v-chip v-if="appointment.isCoupleAppointment" small class="ml-2">
                            <v-icon left small>mdi-human-male-female</v-icon>
                            Paartermin
                        </v-chip>
                    </span>
                </div>
            </v-card-title>
            <v-card-text class="pb-2 text-body-1 black--text">
                <div v-if="appointment.dokumentation && appointment.dokumentation !== '<p></p>'">
                    <TipTapViewer :content="appointment.dokumentation" :expanded="true" />
                </div>
                <span v-else class="font-italic">
                    Dieser Termin wurde noch nicht dokumentiert.
                </span>
                <p v-if="appointment.notizen && appointment.notizen !== '<p></p>'"
                    class="text-subtitle-1 font-weight-bold pb-1 mt-5 mb-0">
                    Persönliche Notizen:
                </p>
                <div v-if="appointment.notizen && appointment.notizen !== '<p></p>'">
                    <TipTapViewer :content="appointment.notizen" :expanded="true" />
                </div>
            </v-card-text>
            <v-card-actions
                :class="$vuetify.breakpoint.smAndDown ? 'd-flex flex-column align-end px-5 pb-5' : 'justify-end px-5 pb-5'">
                <v-row no-gutters v-if="appointmentFileIds.length > 0 && appointmentFileIds.includes(appointment.id)">
                    <v-col
                        v-for="(file) in uploadedFiles.filter(file => file.appointment_id === appointment.id)"
                        :key="'appointment-' + file.id" cols="12" md="6" lg="4">
                        <v-hover v-slot="{ hover }">
                            <v-card class="d-flex align-center px-3 py-1 ma-2 file-card" :elevation="hover ? 2 : 0"
                                outlined @click.stop="$emit('openFile', file)">
                                <v-icon v-if="!file.uploading" class="mr-3" :color="file.iconColor">{{ file.icon
                                    }}</v-icon>
                                <v-progress-circular v-else class="mr-3" :size="30" indeterminate color="primary" />
                                <div class="file-info-container d-flex flex-column justify-center flex-grow-1">
                                    <span class="text-body-2 text-truncate-file">{{ file.name }}</span>
                                    <span class="text-caption">
                                        <v-icon x-small>mdi-clock-outline</v-icon> {{ formatDateTime(file.updated_at) }} ({{ file.size }})
                                    </span>
                                </div>
                            </v-card>
                        </v-hover>
                    </v-col>
                </v-row>
                <v-btn v-if="isEditable && !appointment.isLinked" text @click="$emit('select-files', appointment.id)">Dateien hinzufügen</v-btn>
                <v-btn v-if="isEditable && !appointment.isLinked" outlined @click="editDocumentation(appointment)">Bearbeiten</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-if="allAppointments.length === 0">
            <v-card-text v-if="!loading_client_data && !error_loading_documentation"
                class="d-flex align-center text-body-1 black--text">
                <v-icon left large>
                    mdi-information-outline
                </v-icon>
                <span class="font-italic">
                    Es haben noch keine Termine stattgefunden.
                    Um Termine dokumentieren zu können, müssen zuerst Termine erstellt werden.
                </span>
            </v-card-text>
            <v-card-text v-else-if="!loading_client_data && error_loading_documentation"
                class="d-flex align-center text-body-1 black--text">
                <span class="font-italic">
                    <v-icon left>mdi-connection</v-icon>
                    Die Dokumentation konnte nicht geladen werden.<br />
                    <v-btn outlined :color="$store.state.theme.green" class="mt-5" @click="reloadWindow">
                        <v-icon left>
                            mdi-refresh
                        </v-icon>
                        Neu Laden
                    </v-btn>
                </span>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import connector from '../helpers/supabase-connector.js';
import cipher from '../helpers/cipher.js';
import TipTapEditor from '../components/TipTapEditor.vue';
import TipTapViewer from '../components/TipTapViewer.vue';

export default {
    name: 'ClientDocumentation',
    components: {
        TipTapEditor,
        TipTapViewer,
    },
    emits: ['select-files', 'openFile', 'showError', 'showInfo'],
    props: {
        dialog: {
            type: Boolean,
            default: false
        },
        clientId: {
            type: Number,
            required: true
        },
        clientData: {
            type: Object,
            required: true
        },
        uploadedFiles: {
            type: Array,
            default: () => []
        },
        isEditable: {
            type: Boolean,
            default: true
        },
        clientStatistics: {
            type: Object,
            default: null
        },
        session: Object,
    },

    data() {
        return {
            anamnese: null,
            appointments: [],
            error_loading_anamnese: false,
            error_loading_documentation: false,
            loading_client_data: false,
            sort_appointments_descending: true,
            isFullscreen: false,
            tabs_documentation_type: 0,
            documentation: null,
            notes: null,
            documentation_appointment_id: null,
            dialog_anamnese: false,
            dialog_documentation: false,
            edit_anamnese: null,
            saving: false,
            showFullAnamneseMap: {},
            anamneseIsLongMap: {},
            anonymizeExport: false,
            exporting: false,
            linkedClientsData: {},
            selectedAnamneseTab: null,
            selectedAppointmentClientId: null,
        }
    },

    watch: {
        clientId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.loadClientData();
                } else {
                    this.resetData();
                }
            }
        },
        dialog: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.loadClientData();
                } else {
                    this.resetData();
                }
            }
        }
    },

    computed: {
        appointmentFileIds() {
            let ids = this.uploadedFiles
                .filter(file => file.appointment_id != null)
                .map(file => file.appointment_id);
            return ids;
        },

        selectedClientAppointments() {
            if (!this.appointments) {
                return [];
            }
            const today = dayjs().startOf('day');
            const filteredAppointments = this.appointments.filter(appointment => dayjs(appointment.datum).isBefore(today.add(1, 'day')));
            if (this.sort_appointments_descending) {
                return filteredAppointments.sort((a, b) => dayjs(b.datum) - dayjs(a.datum)).map((appointment, index) => ({
                    ...appointment,
                    appointment_count: filteredAppointments.length - index
                }));
            } else {
                return filteredAppointments.sort((a, b) => dayjs(a.datum) - dayjs(b.datum)).map((appointment, index) => ({
                    ...appointment,
                    appointment_count: index + 1
                }));
            }
        },

        linkedClients() {
            const arr = [];
            const d = this.clientData;
            if (d.fk_vater_id) arr.push({ id: d.fk_vater_id, vorname: d.vater_vorname || '', nachname: d.vater_nachname || '', relation: 'vater' });
            if (d.fk_mutter_id) arr.push({ id: d.fk_mutter_id, vorname: d.mutter_vorname || '', nachname: d.mutter_nachname || '', relation: 'mutter' });
            if (d.fk_partner_id) arr.push({ id: d.fk_partner_id, vorname: d.partner_vorname || '', nachname: d.partner_nachname || '', relation: 'partner' });
            if (Array.isArray(d.kinder)) {
                arr.push(...d.kinder.map(child => ({
                    id: child.id,
                    vorname: child.vorname || '',
                    nachname: child.nachname || '',
                    relation: child.relation || 'kind'
                })));
            }
            return arr;
        },

        allAnamneseTabs() {
            return [
                { id: this.clientId, name: `${this.clientData.vorname || ''} ${this.clientData.nachname || ''}`.trim() || '(Unbenannt)', relation: 'klient' },
                ...this.linkedClients.map(c => ({
                    ...c,
                    name: `${c.vorname || ''} ${c.nachname || ''}`.trim() || '(Unbenannt)'
                }))
            ];
        },

        allAppointments() {
            // Main client appointments: filter, sort, and assign appointment_count
            let mainAppointmentsRaw = (this.appointments || []);
            const today = dayjs().startOf('day');
            let mainFiltered = mainAppointmentsRaw.filter(appt => dayjs(appt.datum).isBefore(today.add(1, 'day')));
            let mainSorted = this.sort_appointments_descending
                ? mainFiltered.sort((a, b) => dayjs(b.datum) - dayjs(a.datum))
                : mainFiltered.sort((a, b) => dayjs(a.datum) - dayjs(b.datum));
            let mainAppointments = mainSorted.map((appt, index) => ({
                ...appt,
                clientId: this.clientId,
                clientName: appt.fk_partner_id 
                    ? `${this.clientData.vorname} ${this.clientData.nachname} & ${appt.partner_vorname} ${appt.partner_nachname}`
                    : `${this.clientData.vorname} ${this.clientData.nachname}`,
                relation: 'klient',
                isLinked: false,
                isCoupleAppointment: !!appt.fk_partner_id,
                appointment_count: this.sort_appointments_descending ? mainSorted.length - index : index + 1
            }));

            // Linked clients: for each, filter, sort, and assign appointment_count
            let linkedAppointments = [];
            for (const client of this.linkedClients) {
                const data = this.linkedClientsData[client.id];
                if (data && data.appointments) {
                    let linkedFiltered = data.appointments.filter(appt => dayjs(appt.datum).isBefore(today.add(1, 'day')));
                    let linkedSorted = this.sort_appointments_descending
                        ? linkedFiltered.sort((a, b) => dayjs(b.datum) - dayjs(a.datum))
                        : linkedFiltered.sort((a, b) => dayjs(a.datum) - dayjs(b.datum));
                    linkedAppointments.push(...linkedSorted.map((appt, index) => ({
                        ...appt,
                        clientId: client.id,
                        clientName: appt.fk_partner_id 
                            ? `${client.vorname} ${client.nachname} & ${appt.partner_vorname} ${appt.partner_nachname}`
                            : `${client.vorname} ${client.nachname}`,
                        relation: client.relation,
                        isLinked: true,
                        isCoupleAppointment: !!appt.fk_partner_id,
                        appointment_count: this.sort_appointments_descending ? linkedSorted.length - index : index + 1
                    })));
                }
            }
            // Merge and sort by date
            return [...mainAppointments, ...linkedAppointments].sort((a, b) =>
                this.sort_appointments_descending
                    ? dayjs(b.datum) - dayjs(a.datum)
                    : dayjs(a.datum) - dayjs(b.datum)
            );
        },

        filteredAppointments() {
            if (!this.selectedAppointmentClientId) {
                return this.allAppointments;
            }
            return this.allAppointments.filter(appt => 
                appt.clientId === this.selectedAppointmentClientId ||
                appt.fk_partner_id === this.selectedAppointmentClientId
            );
        },
    },

    methods: {

        getFormattedDate(date) {
            return dayjs(date).format('DD.MM.YYYY')
        },

        getFormattedTime(time) {
            return dayjs(time, 'HH:mm:ss').format('HH:mm')
        },

        formatDateTime(datetime) {
            if (!datetime) return '';
            return dayjs(datetime).format('DD.MM.YYYY HH:mm');
        },

        reloadWindow() {
            window.location.reload(true);
        },

        handleInput(fieldName, value) {
            this[fieldName] = value;
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },

        async loadClientData() {
            // disable all editing possibilities while loading and indicate loading state
            this.loading_client_data = true;

            // reset error indicators
            this.error_loading_anamnese = false;
            this.error_loading_documentation = false;
            this.showFullAnamneseMap = {};
            this.anamneseIsLongMap = {};
            this.appointments = [];
            this.linkedClientsData = {};

            let anamnese = await connector.getDataOnlyFiltered(this, 'vwdokumentationanamnese', 'eq', 'id', this.clientId, 'id');
            if (anamnese === -1) {
                // error has already been displayed
                this.anamnese = null;
                this.edit_anamnese = null;
                this.error_loading_anamnese = true;
                this.$set(this.showFullAnamneseMap, this.clientId, true);
            } else if (anamnese.length > 0) {
                let dec_anamnese = await cipher.decryptObject(this, this.$store.state.aes_key, anamnese[0]);
                this.anamnese = dec_anamnese.anamnese;
                this.edit_anamnese = this.anamnese;
                // Reset to collapsed state when navigating to a client with long anamnese
                this.$set(this.showFullAnamneseMap, this.clientId, false);
            } else {
                this.anamnese = null;
                this.edit_anamnese = null;
                this.$set(this.showFullAnamneseMap, this.clientId, true); // Show full when empty
            }

            let appointments = await connector.getDataOnlyFiltered(this, 'vwterminedokumentation', 'eq', 'fk_klienten_id', this.clientId, 'datum', false);
            if (appointments === -1) {
                // error has already been shown, we return, so that 
                this.appointments = [];
                this.error_loading_documentation = true;
            } else {
                this.appointments = await cipher.decryptDataSync(this, appointments);
            }


            if (this.linkedClients && this.linkedClients.length > 0) {
                await this.fetchLinkedClientsData();
            }

            // setting at the end, as this triggers the ui change.
            this.loading_client_data = false;
            this.selectedAnamneseTab = this.clientId;
        },

        resetData() {
            this.anamnese = null;
            this.edit_anamnese = null;
            this.appointments = [];
            this.linkedClientsData = {};
            this.selectedAnamneseTab = null;
            this.showFullAnamneseMap = {};
            this.anamneseIsLongMap = {};
        },

        async saveAnamnese() {
            this.saving = true;

            try {
                // save the anamnese to the database
                let enc_anamnese = await cipher.encryptObject(this.$store.state.aes_key, {
                    anamnese: this.edit_anamnese,
                });

                let updated = await connector.update(this, 'klienten', enc_anamnese, this.clientId);
                if (updated === null) {
                    // error has already been shown
                    this.saving = false;
                    return;
                }
                // so that user sees the edited changes
                this.anamnese = this.edit_anamnese;
                this.$refs['editor-anamnese'].clearHistory();
                this.saving = false;
                this.dialog_anamnese = false;
                this.$set(this.showFullAnamneseMap, this.clientId, true); // Always show full content after saving
            } catch (error) {
                await connector.logError(this, {
                    uid: this.session.user.id,
                    message: 'Error saving anamnese ' + error.message,
                });
                this.saving = false;
            }
        },

        editDocumentation(appointment) {
            this.tabs_documentation_type = 0;
            this.documentation = appointment.dokumentation;
            this.notes = appointment.notizen;

            if ('editor-documentation' in this.$refs) {
                this.$refs['editor-documentation'].setContent(this.documentation);
            }

            if ('editor-notes' in this.$refs) {
                this.$refs['editor-notes'].setContent(this.notes);
            }

            this.documentation_appointment_id = appointment.id;
            this.dialog_documentation = true;
        },

        editAnamnese() {
            this.edit_anamnese = this.anamnese;
            if ('editor-anamnese' in this.$refs) {
                this.$refs['editor-anamnese'].setContent(this.edit_anamnese);
            }
            this.dialog_anamnese = true
        },

        abortAnamnese() {
            // reset the anamnese to the last saved value
            this.edit_anamnese = this.anamnese
            this.$refs['editor-anamnese'].clearHistory();
            this.dialog_anamnese = false
        },

        abortDocumentation() {
            this.$refs['editor-documentation'].clearHistory();
            this.$refs['editor-notes'].clearHistory();
            this.dialog_documentation = false;
            this.tabs_documentation_type = 0;
        },

        async saveDocumentation() {
            // save the anamnese to the database
            try {
                this.saving = true;
                let enc_documentation = await cipher.encryptObject(this.$store.state.aes_key, {
                    dokumentation: this.documentation,
                    notizen: this.notes,
                })

                let updated = await connector.update(this, 'termine', enc_documentation, this.documentation_appointment_id);
                if (updated === null) {
                    // error has already been shown
                    this.saving = false;
                    return;
                }

                // update the dokumentation property in this.appointments with the new value where the id matches
                let appointment_index = this.appointments.findIndex(appointment => appointment.id === this.documentation_appointment_id);
                this.appointments[appointment_index].dokumentation = this.documentation;
                this.appointments[appointment_index].notizen = this.notes;

                this.$refs['editor-documentation'].clearHistory();
                this.$refs['editor-notes'].clearHistory();

                // i guess its better to not reset in case something fails, then the user can just try again as the data is still there
                //this.documentation = null
                //this.documentation_appointment_id = null
                this.dialog_documentation = false;
                this.saving = false;
                this.tabs_documentation_type = 0;
            } catch (error) {
                this.saving = false;
                await connector.logError(this, {
                    uid: this.session.user.id,
                    message: 'Error saving documentation ' + error.message,
                });
            }
        },

        toggleAnamneseView() {
            if (this.selectedAnamneseTab) {
                this.$set(this.showFullAnamneseMap, this.selectedAnamneseTab, !this.showFullAnamneseMap[this.selectedAnamneseTab]);
            }
        },

        handleLongContent(isLong) {
            if (this.selectedAnamneseTab) {
                this.$set(this.anamneseIsLongMap, this.selectedAnamneseTab, isLong);
            }
        },

        async exportToWord(includeNotes = false, anonymize = false) {

            // --- Anonymization Logic --- 
            let clientDisplayName = `${this.clientData.vorname} ${this.clientData.nachname}`;
            let clientFilenamePart = `${this.clientData.nachname}_${this.clientData.vorname}`;
            
            if (anonymize && this.clientData.vorname && this.clientData.nachname) {
                const firstInitial = this.clientData.vorname.substring(0, 2);
                const lastInitial = this.clientData.nachname.substring(0, 2);
                clientDisplayName = `${firstInitial}${lastInitial}`;
                clientFilenamePart = clientDisplayName;
            }
            
            try {
                // Create main document container
                const docContainer = document.createElement('div');
                
                // Function to replace mark tags with styled spans
                const replaceMarkTags = (htmlContent) => {
                    if (!htmlContent) return '';
                    // Regex to find <mark> tags, potentially with style attributes, and capture their content
                    // It handles both <mark> and <mark style="...">
                    return htmlContent.replace(/<mark[^>]*?style="[^"]*?background-color:\s*([^;"]+)[^"]*"[^>]*>([\s\S]*?)<\/mark>/gi, (match, color, content) => {
                        // Use the captured color from the style attribute
                        return `<span style="background-color: ${color}; color: inherit;">${content}</span>`;
                    }).replace(/<mark[^>]*>([\s\S]*?)<\/mark>/gi, (match, content) => {
                         // Fallback for <mark> tags without specific inline background color (use default yellow)
                         // Check if the content was already processed by the previous replace to avoid double spans
                        if (match.startsWith('<span')) return match; 
                        return `<span style="background-color: #faf594; color: inherit;">${content}</span>`;
                    });
                };
                
                // Add styles for Word
                const styles = document.createElement('style');
                styles.textContent = `
                    body { font-family: Arial, sans-serif; }
                    h1 { font-size: 18pt; }
                    h2 { font-size: 14pt; border-bottom: 1px solid #eee; padding-bottom: 5px; }
                    h3 { font-size: 12pt; }
                    p { margin-bottom: 8px; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border: 1px solid #ccc; padding: 5px; }
                    th { background-color: #f2f2f2; }
                    .notes { background-color: #f9f9f9; border-left: 3px solid #ccc; padding: 10px; margin-top: 10px; }
                    mark { background-color: #faf594; color: inherit; } /* Keep as fallback, though span is preferred */
                `;
                docContainer.appendChild(styles);
                
                // Prepare content with replaced mark tags
                const processedAnamnese = replaceMarkTags(this.anamnese);

                // Header
                const header = document.createElement('div');
                header.innerHTML = `
                    <h1>Dokumentation: ${clientDisplayName}</h1>
                    <p>Erstellt am: ${dayjs().format('DD.MM.YYYY')}</p>

                    <h2>Übersicht</h2>
                    <p>Erste Einheit: ${this.clientStatistics.first_appointment}</p>
                    <p>Letzte Einheit: ${this.clientStatistics.last_appointment}</p>
                    <p>Anzahl Einheiten: ${this.clientStatistics.appointment_count} (${this.clientStatistics.appointment_sum} Minuten)</p>
                    <p>Frequenz: ${this.clientStatistics.appointment_frequency}x pro Monat</p>

                    <h2>Vorgeschichte / Anamnese</h2>
                    ${processedAnamnese || '<p><i>Es wurde keine Anamnese erfasst.</i></p>'}

                    <h2>Behandlungsverlauf</h2>
                `;
                docContainer.appendChild(header);
                
                // Appointments
                if (this.selectedClientAppointments.length > 0) {
                    this.selectedClientAppointments.forEach(appointment => {
                        const apptSection = document.createElement('div');
                        
                        // Process documentation and notes
                        const processedDokumentation = replaceMarkTags(appointment.dokumentation);
                        const processedNotizen = replaceMarkTags(appointment.notizen);

                        let apptContent = `
                            <h3>${appointment.appointment_count}. Termin am ${dayjs(appointment.datum).format('DD.MM.YYYY')} um ${dayjs(appointment.uhrzeit, 'HH:mm:ss').format('HH:mm')}${appointment.isCoupleAppointment ? ' (Paartermin)' : ''}</h3>
                        `;
                        
                        if (appointment.bezeichnung) {
                            apptContent += `<p><strong>${appointment.bezeichnung}</strong></p>`;
                        }
                        
                        if (processedDokumentation && processedDokumentation !== '<p></p>') {
                            apptContent += processedDokumentation;
                        } else {
                            apptContent += '<p><i>Dieser Termin wurde nicht dokumentiert.</i></p>';
                        }
                        
                        if (includeNotes && processedNotizen && processedNotizen !== '<p></p>') {
                            apptContent += `
                                <div class="notes">
                                    <p><strong>Persönliche Notizen:</strong></p>
                                    ${processedNotizen}
                                </div>
                            `;
                        }
                        
                        apptContent += '<br><hr>';
                        apptSection.innerHTML = apptContent;
                        docContainer.appendChild(apptSection);
                    });
                } else {
                    const noAppts = document.createElement('p');
                    noAppts.innerHTML = '<i>Es haben noch keine Termine stattgefunden.</i>';
                    docContainer.appendChild(noAppts);
                }
                
                // Create HTML content
                const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <title>Dokumentation: ${clientDisplayName}</title>
                    ${styles.outerHTML}
                </head>
                <body>
                    ${docContainer.innerHTML}
                </body>
                </html>
                `;
                
                // Create blob and download
                const blob = new Blob([htmlContent], { type: 'application/msword' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `Dokumentation_${clientFilenamePart}_${dayjs().format('YYYY-MM-DD')}.doc`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                this.$emit('showInfo', {
                    message: 'Die Dokumentation wurde erfolgreich als Word-Dokument exportiert.',
                    timeout: 5000
                });
            } catch (error) {
                console.error('Word export error:', error);
                this.$emit('showError', {
                    message: 'Fehler beim Erstellen des Word-Dokuments. Bitte versuche es erneut oder kontaktiere den Support.',
                    timeout: 10000,
                    error: error
                });
            }
        },

        async triggerExport(includeNotes) {
            this.exporting = true;
            try {
                await this.exportToWord(includeNotes, this.anonymizeExport);
            } catch (error) {
                this.$emit('showError', {
                    message: 'Ein Fehler ist beim Exportieren des Dokuments aufgetreten. Bitte versuche es erneut oder kontaktiere den Support.',
                    timeout: 10000,
                    error: error
                });
            } finally {
                this.exporting = false;
            }
        },

        async fetchLinkedClientsData() {
            const promises = this.linkedClients.map(async (client) => {
                let anamnese = await connector.getDataOnlyFiltered(this, 'vwdokumentationanamnese', 'eq', 'id', client.id, 'id');
                let dec_anamnese = (anamnese !== -1 && anamnese.length > 0)
                    ? await cipher.decryptObject(this, this.$store.state.aes_key, anamnese[0])
                    : { anamnese: null };

                let appointments = await connector.getDataOnlyFiltered(this, 'vwterminedokumentation', 'eq', 'fk_klienten_id', client.id, 'datum', false);
                let dec_appointments = (appointments !== -1)
                    ? await cipher.decryptDataSync(this, appointments)
                    : [];

                this.$set(this.linkedClientsData, client.id, {
                    anamnese: dec_anamnese.anamnese,
                    appointments: dec_appointments,
                    clientData: client
                });
            });
            await Promise.all(promises);
        },

        getAnamneseForTab(clientId) {
            if (clientId === this.clientId) return this.anamnese;
            return this.linkedClientsData[clientId]?.anamnese || null;
        },

        getRelationColor(relation) {
            const relationColors = {
                vater: 'light-blue darken-3',
                mutter: 'pink lighten-1',
                partner: 'teal lighten-1',
                kind: 'purple darken-1',
                klient: 'green darken-1'
            };
            return relationColors[relation] || 'info';
        },

        getRelationIcon(relation) {
            const relationIcons = {
                vater: 'mdi-human-male-girl',
                mutter: 'mdi-human-female-girl',
                partner: 'mdi-human-male-female',
                kind: 'mdi-account-child-outline',
                klient: 'mdi-account-outline'
            };
            return relationIcons[relation] || 'mdi-account-circle-outline';
        },

        getRelationDisplayName(relation) {
            const relationNames = {
                vater: 'Vater',
                mutter: 'Mutter',
                partner: 'Partner',
                kind: 'Kind',
                klient: 'Klient'
            };
            return relationNames[relation] || relation;
        },

        getPrimaryClientName(appointment) {
            if (appointment.isLinked) {
                // For linked appointments, we need to find the primary client name
                // This would be the main client data from the linked client's perspective
                const linkedClient = this.linkedClientsData[appointment.clientId]?.clientData;
                return linkedClient ? `${linkedClient.vorname} ${linkedClient.nachname}` : '';
            } else {
                // For main client appointments, use the current client data
                return `${this.clientData.vorname} ${this.clientData.nachname}`;
            }
        },

        getPartnerName(appointment) {
            if (appointment.partner_vorname && appointment.partner_nachname) {
                return `${appointment.partner_vorname} ${appointment.partner_nachname}`;
            }
            return '';
        },


    },

}
</script>

<style scoped>
.v-sheet.v-card {
  border-radius: 6px;
}
</style>